import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Button } from 'antd';

import isEmpty from 'lodash.isempty';

import axios from '../../../axios';

import { TABLE_CONFIG_KEYS } from '../../../constants/table-configurations';

import { useAlertsColumns } from '../../../hooks/useAlertsColumns';

import { fetchNotifications } from '../../../redux/actions/notifications';
import { setSelectedFilters } from '../../../redux/reducers/filters';
import { setNotificationList, setNotificationState } from '../../../redux/reducers/notifications';
import { setSortedData } from '../../../redux/reducers/notifications';

import AppTable from '../../../components/AppTable';

import { NotificationService } from '../../../services';
import { useFilterOptions } from '../../../views/pages/Notifications/useFilterOptions';

function TableAlerts(props) {
  const dispatch = useDispatch();

  const [filteredInfo, setFilteredInfo] = useState({});

  const { alerts, selectedAlerts, list, loading, sortedData } = useSelector(state => state.notifications);
  const { selectedFilters } = useSelector(state => state.filters);

  useEffect(() => {
    if (alerts?.length) {
      dispatch(setSortedData(alerts));
    }
  }, [alerts]);

  useEffect(() => {
    // on "Clear all filters" set to sortedData that used for calc filter options to original set of data
    if (isEmpty(filteredInfo) && alerts?.length) {
      dispatch(setSortedData(alerts));
    }
  }, [filteredInfo]);

  function selectRecords(ids) {
    dispatch(setNotificationState({ selectedAlerts: ids }));
  }

  const columnFilterOptions = useFilterOptions(sortedData);

  const columns = useAlertsColumns({
    columnFilterOptions,
    filteredInfo,
    onClearClick: props?.onClearClick,
    onSnoozeClick: props?.onSnoozeClick
  });

  const setFilters = (pagination, filters) => {
    setFilteredInfo(filters);
    dispatch(setSelectedFilters(filters));
  };

  const handleTableChange = useCallback(
    (pagination, filters, sorter, { currentDataSource }) => {
      setFilters(pagination, filters);
      dispatch(setSortedData(currentDataSource));
    },
    [setSortedData, dispatch]
  );

  const clearFilters = () => {
    setFilteredInfo({});
    dispatch(setSelectedFilters({}));
  };

  async function clearAllNotifications() {
    const response = await NotificationService.clearAllNotifications();

    if (response.status === 200) {
      dispatch(setNotificationList([]));
      dispatch(fetchNotifications());
    }
  }

  async function snoozeAllNotifications() {
    const response = await NotificationService.snoozeAllNotifications();

    if (response.status === 200) dispatch(fetchNotifications());
  }

  async function clearNotifications() {
    const selectedNotifs = selectedAlerts;

    if (selectedNotifs.length) {
      const response = await axios.put('notifications/clear', {
        ids: selectedNotifs
      });

      if (response.status === 200) {
        const updatedNotificationList = list?.filter(notif => !selectedNotifs.includes(notif.id));
        dispatch(setNotificationList(updatedNotificationList));
        // dispatch(setNotificationState({ selectedAlerts: [] }));
        dispatch(fetchNotifications());
      }
    }
  }

  async function snoozeNotifications() {
    if (selectedAlerts?.length) {
      const response = await axios.put('notifications/snooze', {
        ids: selectedAlerts,
        force: true,
        seconds: 86400
      });

      if (response.status === 200) {
        // dispatch(setNotificationState({ selectedAlerts: [] }));
        dispatch(fetchNotifications());
      }
    }
  }

  const actions = useMemo(() => {
    const isAnyAlertSelected = selectedAlerts?.length;

    return [
      {
        label: (
          <Button
            color="default"
            size="small"
            variant="link"
            disabled={!isAnyAlertSelected}
            onClick={clearNotifications}>
            Clear notifications
          </Button>
        )
      },
      {
        label: (
          <Button
            color="default"
            size="small"
            variant="link"
            disabled={!isAnyAlertSelected}
            onClick={snoozeNotifications}>
            Snooze notifications
          </Button>
        )
      },
      {
        label: (
          <Button color="default" size="small" variant="link" onClick={clearAllNotifications}>
            Clear all new notifications
          </Button>
        )
      },
      {
        label: (
          <Button color="default" size="small" variant="link" onClick={snoozeAllNotifications}>
            Snooze all new notifications
          </Button>
        )
      },
      {
        label: (
          <Button
            color="default"
            size="small"
            variant="link"
            disabled={!Object.values(selectedFilters)?.length}
            onClick={clearFilters}>
            Clear all filters
          </Button>
        )
      }
    ];
  }, [selectedAlerts, selectedFilters]);

  return (
    <AppTable
      headerClass="py-2"
      actions={actions}
      baseColumns={columns}
      dataSource={alerts}
      filterable={false}
      loading={loading}
      rowClassName={record => (record.cleared_at ? 'ant-table-row-accent' : '')}
      searchable={false}
      tableConfigKey={TABLE_CONFIG_KEYS.ALERTS}
      handleOnChange={handleTableChange}
      handleOnSelectRecords={selectRecords}
    />
  );
}

export default memo(TableAlerts);
